// ---------------------------------------------------------------------------
// LockMenu — header dropdown: Lock Now / Change Password / Remove Password
// ---------------------------------------------------------------------------

import { useState, useEffect, useRef, type FormEvent } from 'react';
import type { AppLockInstance } from '@ascend/applock';

interface LockMenuProps {
  lock: AppLockInstance;
}

type Panel = 'main' | 'change' | 'remove';

export function LockMenu({ lock }: LockMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>('main');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Change-password form state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  // Remove-password form state
  const [removePw, setRemovePw] = useState('');

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const resetForms = () => {
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setRemovePw('');
    setError(null);
    setInfo(null);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setPanel('main');
    resetForms();
  };

  const handleLockNow = () => {
    lock.lock('manual');
    closeMenu();
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setInfo(null);
    if (newPw.length < 4) {
      setError('New password must be at least 4 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await lock.changePassword(currentPw, newPw);
      setInfo('Password changed successfully.');
      resetForms();
    } catch (err) {
      console.error('Change password failed:', err);
      setError('Current password is incorrect.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await lock.removePassword(removePw);
      // After removal, data is decrypted back to cleartext. Close the menu;
      // the app stays unlocked (decision remains 'enabled' but isConfigured
      // flips to false via the 'password:clear' event).
      closeMenu();
    } catch (err) {
      console.error('Remove password failed:', err);
      setError('Current password is incorrect.');
      setBusy(false);
    }
  };

  const buttonClass =
    'flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text)] transition-[var(--transition-fast)]';

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => {
          if (!isOpen) {
            resetForms();
            setPanel('main');
          }
          setIsOpen(!isOpen);
        }}
        className="rounded-md p-2 text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:bg-[var(--color-bg-tertiary)]"
        aria-label="App Lock settings"
        aria-expanded={isOpen}
        title="App Lock"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-72 rounded-lg border border-border bg-surface shadow-lg">
          {panel === 'main' && (
            <div className="p-2">
              <p className="px-3 py-1.5 text-xs font-semibold text-[var(--color-text)]">
                App Lock
              </p>
              <button onClick={handleLockNow} className={buttonClass}>
                <LockIcon /> Lock now
              </button>
              <button onClick={() => { resetForms(); setPanel('change'); }} className={buttonClass}>
                <KeyIcon /> Change password
              </button>
              <button onClick={() => { resetForms(); setPanel('remove'); }} className={buttonClass}>
                <TrashIcon /> Remove password
              </button>
            </div>
          )}

          {panel === 'change' && (
            <form onSubmit={handleChangePassword} className="space-y-2 p-3">
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-semibold text-[var(--color-text)]">Change password</p>
                <button type="button" onClick={() => { resetForms(); setPanel('main'); }} className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                  Back
                </button>
              </div>
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Current password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="w-full rounded border border-border px-2 py-1.5 text-xs"
                disabled={busy}
                autoFocus
              />
              <input
                type="password"
                autoComplete="new-password"
                placeholder="New password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full rounded border border-border px-2 py-1.5 text-xs"
                disabled={busy}
              />
              <input
                type="password"
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full rounded border border-border px-2 py-1.5 text-xs"
                disabled={busy}
              />
              {error && <p className="text-xs text-error">{error}</p>}
              {info && <p className="text-xs text-success">{info}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="flex-1 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                >
                  {busy ? 'Updating…' : 'Update'}
                </button>
                <button
                  type="button"
                  onClick={closeMenu}
                  className="rounded border border-border px-3 py-1.5 text-xs text-[var(--color-text-muted)]"
                >
                  Close
                </button>
              </div>
            </form>
          )}

          {panel === 'remove' && (
            <form onSubmit={handleRemovePassword} className="space-y-2 p-3">
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-semibold text-[var(--color-text)]">Remove password</p>
                <button type="button" onClick={() => { resetForms(); setPanel('main'); }} className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                  Back
                </button>
              </div>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Your data will be decrypted back to cleartext storage. App Lock
                protection will be turned off.
              </p>
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Current password"
                value={removePw}
                onChange={(e) => setRemovePw(e.target.value)}
                className="w-full rounded border border-border px-2 py-1.5 text-xs"
                disabled={busy}
                autoFocus
              />
              {error && <p className="text-xs text-error">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="flex-1 rounded bg-error px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                >
                  {busy ? 'Removing…' : 'Remove'}
                </button>
                <button
                  type="button"
                  onClick={closeMenu}
                  className="rounded border border-border px-3 py-1.5 text-xs text-[var(--color-text-muted)]"
                >
                  Close
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function LockIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
